import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import type { Task, UserPublic } from '@underline/shared';
import { TaskCard } from '@/components/tasks/task-card';
import { UserAvatar } from '@/components/users/user-avatar';
import { EmptyState } from '@/components/ui/empty-state';
import { useTasks, useToggleTask } from '@/hooks/use-tasks';
import { useUsers } from '@/hooks/use-users';
import { isOverdue, slugify } from '@/lib/dates';

export function TasksByUserPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: users = [] } = useUsers();
  const { data: tasks = [] } = useTasks();
  const toggle = useToggleTask();

  const safeSlug = slug ?? '';
  const user = users.find((u) => {
    const first = u.name.split(' ')[0] ?? u.name;
    return slugify(u.name).startsWith(safeSlug) || slugify(first) === safeSlug;
  });

  if (!user) {
    return (
      <div>
        <button
          onClick={() => navigate('/settings')}
          className="text-sm text-muted-foreground inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar
        </button>
        <EmptyState title="Usuário não encontrado" />
      </div>
    );
  }

  const mine = tasks.filter((t) => t.responsible_id === user.id);
  const pending = mine.filter(
    (t) => t.status === 'pending' && !isOverdue(t.due_date, t.due_time),
  );
  const overdue = mine.filter(
    (t) => t.status === 'pending' && isOverdue(t.due_date, t.due_time),
  );
  const done = mine.filter((t) => t.status === 'done');

  return (
    <div>
      <button
        onClick={() => navigate('/settings')}
        className="text-sm text-muted-foreground inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar
      </button>

      <header className="flex items-center gap-4 mb-6">
        <UserAvatar
          name={user.name}
          colorHex={user.color_hex}
          url={user.avatar_url}
          size="xl"
        />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{user.name}</h1>
          <p className="mono text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">
            {user.role}
          </p>
        </div>
      </header>

      <Section
        title={`Atrasadas (${overdue.length})`}
        tasks={overdue}
        user={user}
        onToggle={(id) => toggle.mutate(id)}
      />
      <Section
        title={`Pendentes (${pending.length})`}
        tasks={pending}
        user={user}
        onToggle={(id) => toggle.mutate(id)}
      />
      <Section
        title={`Feitas (${done.length})`}
        tasks={done}
        user={user}
        onToggle={(id) => toggle.mutate(id)}
      />
    </div>
  );
}

function Section({
  title,
  tasks,
  user,
  onToggle,
}: {
  title: string;
  tasks: Task[];
  user: UserPublic;
  onToggle: (id: number) => void;
}) {
  if (tasks.length === 0) return null;
  return (
    <section className="mb-6">
      <h2 className="text-sm font-semibold mb-3 uppercase tracking-wider mono">{title}</h2>
      <div className="space-y-3">
        <AnimatePresence initial={false}>
          {tasks.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              responsible={user}
              onToggle={() => onToggle(t.id)}
            />
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
}
